import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material';
import { select, Store } from '@ngrx/store';
import { ChartsExpensesByCategorySelect } from 'app/main/charts/charts.actions';
import { CubeDetailDialogComponent } from 'app/main/cube/cube-detail/cube-detail-dialog.component';
import { selectCubeByProvider } from 'app/main/cube/cube.selectors';
import { AppState } from 'app/reducers';
import { Observable, Subject } from 'rxjs';
import { first, withLatestFrom, takeUntil } from 'rxjs/operators';

import {
    selectChartsExpensesByCategory,
    selectChartsExpensesByCategoryDetail,
    selectUIChartsExpensesByCategory
} from './charts-expenses-by-category.selectors';
import { ActivatedRoute } from '@angular/router';
import { CubeRow } from 'app/main/cube/cube.component';
import { CompanySubset } from 'app/api/company.api.service';
import { CompaniesSelectors, CompaniesUISelectors } from 'app/main/company/selectors';

export interface ChartsOptions {
    animations?: boolean;
    doughnut?: boolean;
    gradient?: boolean;
    labels?: boolean;
    legend?: boolean;
    legendTitle?: string;
    legendPosition?: string; // 'below'
    scheme?: { domain: string[] } | string;
    showXAxisLabel?: boolean;
    showYAxisLabel?: boolean;
    view?: any[] | string;
    xAxis?: boolean;
    xAxisLabel?: string;
    yAxis?: boolean;
    yAxisLabel?: string;
}

@Component({
    selector: 'app-charts-expenses-by-category',
    templateUrl: './charts-expenses-by-category.component.html',
    styleUrls: ['./charts-expenses-by-category.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ChartsExpensesByCategoryComponent implements OnInit, OnDestroy {
    public pageTitle: string;
    public expensesByCategory: ChartsOptions = {
        animations: true,
        gradient: false,
        legend: true,
        legendTitle: 'CA par Categorie',
        legendPosition: 'right',
        scheme: 'forest',
        showXAxisLabel: true,
        showYAxisLabel: true,
        xAxis: true,
        xAxisLabel: '', // Timeline
        yAxis: true,
        yAxisLabel: 'Montants',
    };
    public detail: ChartsOptions = {
        animations: true,
        gradient: false,
        legend: false,
        legendTitle: '',
        scheme: 'forest',
        showXAxisLabel: true,
        showYAxisLabel: true,
        xAxis: true,
        xAxisLabel: '',
        yAxis: true,
        yAxisLabel: '',
    };

    public expensesByCategoryResults$: Observable<any[]>;
    public detailResults$: Observable<any[]>;
    public uiExpensesByCategory$: Observable<{ providerCategory: string, timeline: string }>;

    public company: CompanySubset;

    private _unsubscribeAll: Subject<any>;

    constructor(
        protected store: Store<AppState>,
        protected activatedRoute: ActivatedRoute,
        public dialog: MatDialog
    ) {
        this._unsubscribeAll = new Subject();
        this.pageTitle = activatedRoute.snapshot.data['title'];
    }

    ngOnInit(): void {
        this.uiExpensesByCategory$ = this.store.pipe(select(selectUIChartsExpensesByCategory));
        this.expensesByCategoryResults$ = this.store.pipe(select(selectChartsExpensesByCategory));
        this.detailResults$ = this.store.pipe(select(selectChartsExpensesByCategoryDetail));

        this.uiExpensesByCategory$
            .subscribe(expensesByCategory => {
                if (expensesByCategory.providerCategory !== null) {
                    this.detail.legendTitle = `TOP 12 ${expensesByCategory.providerCategory}`;
                }
            });

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(CompaniesSelectors.selectCurrentCompany)
            )
            .subscribe(company => this.company = company);

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(CompaniesUISelectors.selectCurrentCompanyId),
            )
            .subscribe(companyId => {
                window.history.pushState({}, '', `/charts/${companyId}/expenses-by-category`);
            });
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    onSelectCategory(event): void {
        if (event && event.series) {
            this.store.dispatch(new ChartsExpensesByCategorySelect({
                timeline: event.series,
                providerCategory: event.name
            }));
        }
    }

    // onSelectExpensesByCategory

    onSelectDetail(event): void {
        if (event && event.name) {
            this.store
                .pipe(
                    takeUntil(this._unsubscribeAll),
                    select(selectCubeByProvider(event.name)),
                    withLatestFrom(this.store.pipe(select(selectUIChartsExpensesByCategory))),
                    first()
                )
                .subscribe(([cube, expensesByCategory]) => {
                    if (cube && cube.length > 0) {
                        const data: { company: CompanySubset, title: string, labels: string[], rows: CubeRow[] } = {
                            company: this.company,
                            title: this.pageTitle,
                            // tslint:disable-next-line: max-line-length
                            labels: [expensesByCategory.providerCategory, event.name, `(${expensesByCategory.timeline})`],
                            rows: cube.filter(c => c.timeline === expensesByCategory.timeline
                                && c.providerCategory === expensesByCategory.providerCategory
                            )
                        };

                        this.dialog.open(CubeDetailDialogComponent, {
                            maxWidth: '95vw',
                            maxHeight: '95vh',
                            width: '85vw',
                            height: '85vh',
                            data
                        });
                    }
                });
        }
    }

}
